import { createElement, type ComponentType, type ImgHTMLAttributes, type ReactNode } from "react";
import type { IImageComponentProps } from "@newxsystems/types";

export type KaajImageAspectRatio =
  | "3/4"
  | "2/3"
  | "3/2"
  | "4/3"
  | "16/9"
  | "9/16"
  | "5/2"
  | "square"
  | "fullwidth"
  | "infographic";

export type KaajImagePrimitiveProps = ImgHTMLAttributes<HTMLImageElement> & {
  alt: string;
  aspectRatio?: KaajImageAspectRatio;
  aspectRatioClassName?: string;
  caption?: string;
  crop?: IImageComponentProps["crop"];
  height?: number | `${number}`;
  imageClassName?: string;
  priority?: boolean;
  responsiveProfile?: "default" | "compact";
  src: string;
  width?: number | `${number}`;
};

export type KaajLinkPrimitiveProps = {
  children?: ReactNode;
  className?: string;
  href: string;
  target?: string;
  rel?: string;
  tabIndex?: number;
  title?: string;
  "aria-label"?: string;
};

export type KaajPrimitiveRegistry = {
  Image: ComponentType<KaajImagePrimitiveProps>;
  Link: ComponentType<KaajLinkPrimitiveProps>;
};

export type KaajPrimitiveName = keyof KaajPrimitiveRegistry;

const FallbackImage = ({
  alt,
  aspectRatio: _aspectRatio,
  aspectRatioClassName: _aspectRatioClassName,
  caption: _caption,
  className,
  crop: _crop,
  imageClassName,
  priority: _priority,
  responsiveProfile: _responsiveProfile,
  ...props
}: KaajImagePrimitiveProps) =>
  createElement("img", {
    alt,
    className: [className, imageClassName].filter(Boolean).join(" ") || undefined,
    ...props,
  });

const FallbackLink = ({ children, rel, target, ...props }: KaajLinkPrimitiveProps) => {
  const safeRel = target === "_blank" && rel === undefined ? "noopener noreferrer" : rel;

  return createElement("a", { rel: safeRel, target, ...props }, children);
};

const registry: KaajPrimitiveRegistry = {
  Image: FallbackImage,
  Link: FallbackLink,
};

export function registerKaajPrimitive<TName extends KaajPrimitiveName>(
  name: TName,
  component: KaajPrimitiveRegistry[TName],
) {
  registry[name] = component;
}

export function registerKaajPrimitives(components: Partial<KaajPrimitiveRegistry>) {
  if (components.Image) {
    registerKaajPrimitive("Image", components.Image);
  }

  if (components.Link) {
    registerKaajPrimitive("Link", components.Link);
  }
}

export function resolveKaajPrimitive<TName extends KaajPrimitiveName>(
  name: TName,
): KaajPrimitiveRegistry[TName] {
  return registry[name];
}

export const registerComponent = registerKaajPrimitive;
export const getComponent = resolveKaajPrimitive;
